/**
 * Utility functions for extracting titles and other document information
 */

/**
 * Extracts a meaningful title from document metadata with fallbacks
 * @param {Object} document - The document object
 * @param {Object} document.metadata - Document metadata
 * @param {string} document.content - Document content
 * @param {Object} options - Configuration options
 * @param {number} options.maxContentLength - Maximum length for content-based title (default: 80)
 * @param {string} options.fallback - Fallback title if nothing is found (default: 'Untitled Document')
 * @returns {string} The extracted title
 */
export const extractDocumentTitle = (document, options = {}) => {
  const { maxContentLength = 80, fallback = 'Untitled Document' } = options;

  if (!document) return fallback;

  // Define title field priority order
  const titleFields = [
    'title',
    'name',
    'heading',
    'subject',
    'article_title',
    'room_name',
    'venue',
    'display_name',
    'label',
    'caption',
    'description',
  ];

  // Try to extract from metadata first
  if (document.metadata && typeof document.metadata === 'object') {
    const foundField = titleFields.find(field => {
      const value = document.metadata[field];
      return value && typeof value === 'string' && value.trim().length > 0;
    });

    if (foundField) {
      return document.metadata[foundField].trim();
    }
  }

  // Fallback to content extraction
  if (document.content && typeof document.content === 'string') {
    const content = document.content.trim();
    if (content.length > 0) {
      // Get first line or sentence
      const firstLine = content.split('\n')[0].trim();
      const firstSentence = firstLine.split('.')[0].trim();

      // Use first sentence if it's not too long, otherwise first line
      const candidateTitle =
        firstSentence.length <= maxContentLength ? firstSentence : firstLine;

      if (candidateTitle.length > 0) {
        return candidateTitle.length > maxContentLength
          ? `${candidateTitle.substring(0, maxContentLength)}...`
          : candidateTitle;
      }
    }
  }

  // Also try page_content if it exists (for search results)
  if (document.page_content && typeof document.page_content === 'string') {
    const content = document.page_content.trim();
    if (content.length > 0) {
      const firstLine = content.split('\n')[0].trim();
      if (firstLine.length > 0) {
        return firstLine.length > maxContentLength
          ? `${firstLine.substring(0, maxContentLength)}...`
          : firstLine;
      }
    }
  }

  return fallback;
};

/**
 * Gets a shorter title suitable for compact displays
 * @param {Object} document - The document object
 * @param {number} maxLength - Maximum length for the title (default: 50)
 * @returns {string} The shortened title
 */
export const getShortTitle = (document, maxLength = 50) => {
  const title = extractDocumentTitle(document, { maxContentLength: maxLength });

  if (title.length <= maxLength) {
    return title;
  }

  return `${title.substring(0, maxLength)}...`;
};

/**
 * Extracts subtitle or secondary information from document
 * @param {Object} document - The document object
 * @returns {string|null} The subtitle or null if none found
 */
export const extractDocumentSubtitle = document => {
  if (!document?.metadata) return null;

  const subtitleFields = [
    'subtitle',
    'description',
    'summary',
    'venue',
    'location',
    'category',
    'type',
  ];

  const foundField = subtitleFields.find(field => {
    const value = document.metadata[field];
    return value && typeof value === 'string' && value.trim().length > 0;
  });

  return foundField ? document.metadata[foundField].trim() : null;
};

/**
 * Gets document type icon based on content and metadata
 * @param {Object} document - The document object
 * @returns {string} The icon class name
 */
export const getDocumentIcon = document => {
  if (!document) return 'i-lucide-file-text';

  // Check metadata for type information
  if (document.metadata?.INTERNAL_ROOKOO_TYPE === 'image') {
    return 'i-lucide-image';
  }

  if (document.metadata?.type) {
    const type = document.metadata.type.toLowerCase();
    switch (type) {
      case 'image':
      case 'photo':
      case 'picture':
        return 'i-lucide-image';
      case 'video':
      case 'movie':
        return 'i-lucide-video';
      case 'audio':
      case 'music':
      case 'sound':
        return 'i-lucide-music';
      case 'pdf':
      case 'document':
        return 'i-lucide-file-text';
      case 'spreadsheet':
      case 'excel':
        return 'i-lucide-table';
      case 'presentation':
      case 'powerpoint':
        return 'i-lucide-presentation';
      default:
        break;
    }
  }

  // Check content for URL patterns
  const content = document.content || document.page_content || '';
  if (content && content.includes('http')) {
    return 'i-lucide-link';
  }

  // Check filename extension if available
  if (document.metadata?.source || document.metadata?.filename) {
    const filename = (
      document.metadata.source || document.metadata.filename
    ).toLowerCase();
    if (filename.match(/\.(jpg|jpeg|png|gif|svg|webp)$/)) {
      return 'i-lucide-image';
    }
    if (filename.match(/\.(mp4|avi|mov|wmv|flv|webm)$/)) {
      return 'i-lucide-video';
    }
    if (filename.match(/\.(mp3|wav|flac|aac|ogg)$/)) {
      return 'i-lucide-music';
    }
    if (filename.match(/\.(pdf)$/)) {
      return 'i-lucide-file-text';
    }
    if (filename.match(/\.(xls|xlsx|csv)$/)) {
      return 'i-lucide-table';
    }
    if (filename.match(/\.(ppt|pptx)$/)) {
      return 'i-lucide-presentation';
    }
  }

  return 'i-lucide-file-text';
};

/**
 * Checks if document is an image type
 * @param {Object} document - The document object
 * @returns {boolean} True if document is an image
 */
export const isImageDocument = document => {
  if (!document) return false;

  return (
    document.metadata?.INTERNAL_ROOKOO_TYPE === 'image' ||
    getDocumentIcon(document) === 'i-lucide-image'
  );
};

/**
 * Formats content with length limit and ellipsis
 * @param {string} content - The content to format
 * @param {number} maxLength - Maximum length (default: 150)
 * @returns {string} Formatted content
 */
export const formatContent = (content, maxLength = 150) => {
  if (!content || typeof content !== 'string') return '';

  const trimmed = content.trim();
  return trimmed.length > maxLength
    ? `${trimmed.substring(0, maxLength)}...`
    : trimmed;
};

/**
 * Formats a source name by removing file extension
 * @param {string} source - The source string
 * @returns {string} Formatted source name
 */
export const formatSourceName = source => {
  if (!source || typeof source !== 'string') return '';

  const parts = source.split('.');
  return parts.length > 1 ? parts.slice(0, -1).join('.') : source;
};
